
import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { UserTable } from '@/components/users/user-table';
import { AddUserDialog } from '@/components/users/add-user-dialog';
import { PlusIcon } from 'lucide-react';
import { Toggle } from '@/components/ui/toggle';

export default function UsersPage() {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showPartiallyPaidOnly, setShowPartiallyPaidOnly] = useState(false);
  const userTableRef = useRef<{ fetchUsers: () => Promise<void> } | null>(null);

  const handleUserAdded = () => {
    // Refresh the user list
    if (userTableRef.current) {
      userTableRef.current.fetchUsers();
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h1 className="text-3xl font-bold">{t('users.title')}</h1>

        <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto">
          <Input
            placeholder={t('users.search')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full sm:w-64"
          />

          <Toggle
            pressed={showPartiallyPaidOnly}
            onPressedChange={setShowPartiallyPaidOnly}
            aria-label={t('users.filterPartiallyPaid')}
            className="whitespace-nowrap"
          >
            {t('users.partiallyPaidOnly', 'Partially Paid Only')}
          </Toggle>

          <Button onClick={() => setShowAddDialog(true)}>
            <PlusIcon className="h-4 w-4 mr-2" />
            {t('users.addUser')}
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t('users.usersList')}</CardTitle>
        </CardHeader>
        <CardContent>
          <UserTable
            searchQuery={searchQuery}
            showPartiallyPaidOnly={showPartiallyPaidOnly}
            ref={userTableRef}
          />
        </CardContent>
      </Card>

      <AddUserDialog
        open={showAddDialog}
        onOpenChange={setShowAddDialog}
        onSuccess={handleUserAdded}
      />
    </div>
  );
}
